import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { Lock, Download, RefreshCw, ArrowRight, Users, Mail, CheckCircle2, MousePointerClick, Calculator } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUSES = ["New", "Contacted", "Meeting Booked", "Signed", "Passed"];
const STATUS_COLORS = {
  New: "text-epic-sub",
  Contacted: "text-blue-400",
  "Meeting Booked": "text-yellow-400",
  Signed: "text-green-400",
  Passed: "text-epic-sub/50",
};

export default function AdminLeads() {
  const [pw, setPw] = useState(sessionStorage.getItem("epic_admin_pw") || "");
  const [authed, setAuthed] = useState(false);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [clicks, setClicks] = useState([]);
  const [scenarios, setScenarios] = useState([]);

  const fetchLeads = useCallback(async (password) => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API}/admin/leads`, {
        headers: { "X-Admin-Password": password },
      });
      if (res.status === 401) {
        setError("Incorrect password.");
        setAuthed(false);
        sessionStorage.removeItem("epic_admin_pw");
        return;
      }
      const data = await res.json();
      setLeads(data);
      setAuthed(true);
      sessionStorage.setItem("epic_admin_pw", password);
      try {
        const cres = await fetch(`${API}/admin/clicks`, {
          headers: { "X-Admin-Password": password },
        });
        if (cres.ok) setClicks(await cres.json());
        const sres = await fetch(`${API}/admin/scenarios`, {
          headers: { "X-Admin-Password": password },
        });
        if (sres.ok) setScenarios(await sres.json());
      } catch (e) {
        // click/scenario stats are non-critical
      }
    } catch (e) {
      setError("Could not reach the server. Try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (pw) fetchLeads(pw);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogin = (e) => {
    e.preventDefault();
    if (pw.trim()) fetchLeads(pw.trim());
  };

  const handleExport = async () => {
    try {
      const res = await fetch(`${API}/admin/leads.csv`, {
        headers: { "X-Admin-Password": pw },
      });
      if (!res.ok) {
        setError(res.status === 401 ? "Session expired — please log in again." : "Export failed. Try again.");
        if (res.status === 401) { setAuthed(false); sessionStorage.removeItem("epic_admin_pw"); }
        return;
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "epic_investor_leads.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError("Export failed. Try again.");
    }
  };

  const fmt = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleString("en-US", {
        month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  const patchLead = async (id, changes) => {
    setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, ...changes } : l)));
    try {
      const res = await fetch(`${API}/admin/leads/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", "X-Admin-Password": pw },
        body: JSON.stringify(changes),
      });
      if (res.ok) {
        const updated = await res.json();
        setLeads((prev) => prev.map((l) => (l.id === id ? updated : l)));
      }
    } catch (e) {
      setError("Could not save changes.");
    }
  };

  const [digestMsg, setDigestMsg] = useState("");
  const sendDigest = async () => {
    setDigestMsg("Sending…");
    try {
      const res = await fetch(`${API}/admin/send-digest`, {
        method: "POST",
        headers: { "X-Admin-Password": pw },
      });
      const data = await res.json();
      setDigestMsg(data.count ? `Digest sent — ${data.count} lead(s).` : "No new leads in the last 24h.");
    } catch (e) {
      setDigestMsg("Digest failed. Try again.");
    }
    setTimeout(() => setDigestMsg(""), 5000);
  };

  if (!authed) {
    return (
      <div data-testid="admin-login" className="relative min-h-screen w-full bg-epic-bg flex items-center justify-center px-5">
        <div className="absolute top-0 left-0 right-0 h-px bg-epic-accent" />
        <motion.form
          onSubmit={handleLogin}
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="w-full max-w-sm"
        >
          <div className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.25em] text-epic-sub mb-8">
            <Lock className="w-4 h-4 text-epic-accent" />
            ePIC — Leads Dashboard
          </div>
          <h1 className="font-grotesk font-black uppercase tracking-tighter text-epic-text leading-[0.9] mb-8 text-4xl md:text-5xl">
            Admin<br /><span className="text-epic-accent">access.</span>
          </h1>
          <input
            type="password"
            data-testid="admin-password-input"
            placeholder="Access password"
            value={pw}
            onChange={(e) => { setPw(e.target.value); setError(""); }}
            className="w-full bg-epic-surface border border-epic-line focus:border-epic-accent outline-none px-4 py-3.5 font-mono text-sm text-epic-text placeholder:text-epic-sub/50 transition-colors duration-200"
          />
          {error && <p data-testid="admin-error" className="font-mono text-[11px] text-epic-accent mt-3">{error}</p>}
          <button
            type="submit"
            data-testid="admin-login-btn"
            disabled={loading}
            className="group mt-6 inline-flex items-center gap-3 bg-epic-accent px-8 py-4 text-white font-mono text-xs uppercase tracking-[0.15em] transition-[background-color,transform] duration-300 hover:bg-epic-accentHover active:scale-95 disabled:opacity-60"
          >
            {loading ? "Checking…" : "Enter Dashboard"}
            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
          </button>
        </motion.form>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="relative min-h-screen w-full bg-epic-bg">
      <div className="absolute top-0 left-0 right-0 h-px bg-epic-accent" />
      <div className="mx-auto max-w-[1200px] px-5 md:px-10 py-16 md:py-24">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <div className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.25em] text-epic-sub mb-5">
              <Users className="w-4 h-4 text-epic-accent" />
              Investor Leads
            </div>
            <h1 className="font-grotesk font-black uppercase tracking-tighter text-epic-text leading-[0.9] text-4xl md:text-6xl">
              {leads.length} <span className="text-epic-accent">Lead{leads.length === 1 ? "" : "s"}</span>
            </h1>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={sendDigest}
              data-testid="admin-send-digest"
              className="inline-flex items-center gap-2 border border-epic-line px-5 py-3.5 text-epic-text font-mono text-xs uppercase tracking-[0.15em] transition-colors duration-300 hover:border-epic-lineHover"
            >
              <Mail className="w-4 h-4" />
              Send Digest
            </button>
            <button
              onClick={() => fetchLeads(pw)}
              data-testid="admin-refresh"
              className="inline-flex items-center gap-2 border border-epic-line px-5 py-3.5 text-epic-text font-mono text-xs uppercase tracking-[0.15em] transition-colors duration-300 hover:border-epic-lineHover"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </button>
            <button
              onClick={handleExport}
              data-testid="admin-export-csv"
              className="inline-flex items-center gap-2 bg-epic-accent px-5 py-3.5 text-white font-mono text-xs uppercase tracking-[0.15em] transition-[background-color,transform] duration-300 hover:bg-epic-accentHover active:scale-95"
            >
              <Download className="w-4 h-4" />
              Export CSV
            </button>
          </div>
        </div>

        {digestMsg && <p data-testid="admin-digest-msg" className="font-mono text-[11px] text-green-400 mb-4">{digestMsg}</p>}

        {error && <p data-testid="admin-error" className="font-mono text-[11px] text-epic-accent mb-4">{error}</p>}

        <div data-testid="admin-clicks" className="border border-epic-line bg-epic-surface p-5 md:p-6 mb-8">
          <div className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.25em] text-epic-sub mb-4">
            <MousePointerClick className="w-4 h-4 text-epic-accent" />
            Outbound clicks — partner & engine links
          </div>
          {clicks.length === 0 ? (
            <p className="font-mono text-xs text-epic-sub/50">No outbound clicks tracked yet.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {clicks.map((c) => (
                <span
                  key={c.target}
                  data-testid={`admin-click-${c.target}`}
                  className="border border-epic-line px-3 py-1.5 font-mono text-xs text-epic-sub"
                >
                  {c.target} <span className="text-epic-accent font-semibold">{c.count}</span>
                </span>
              ))}
            </div>
          )}
        </div>

        <div data-testid="admin-scenarios" className="border border-epic-line bg-epic-surface p-5 md:p-6 mb-8">
          <div className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.25em] text-epic-sub mb-4">
            <Calculator className="w-4 h-4 text-epic-accent" />
            Calculator scenario emails
          </div>
          {scenarios.length === 0 ? (
            <p className="font-mono text-xs text-epic-sub/50">No calculator scenarios emailed yet.</p>
          ) : (
            <div className="space-y-2">
              {scenarios.map((s, i) => (
                <div
                  key={`${s.ts}-${i}`}
                  data-testid={`admin-scenario-${i}`}
                  className="flex flex-wrap items-center gap-3 font-mono text-xs text-epic-sub border-b border-epic-line/50 pb-2 last:border-0"
                >
                  <span
                    className={`px-2 py-0.5 border text-[10px] uppercase tracking-widest ${
                      s.type === "note" ? "border-epic-accent text-epic-accent" : "border-epic-line text-epic-text"
                    }`}
                  >
                    {s.type}
                  </span>
                  <span className="text-epic-text">{s.email}</span>
                  <span>
                    ${Number(s.amount).toLocaleString()}
                    {s.type === "note" ? ` · ${s.bonus * 100}% bonus` : " · equity"}
                  </span>
                  <span className="text-epic-sub/50 ml-auto">{new Date(s.ts).toLocaleString()}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="border border-epic-line bg-epic-surface overflow-x-auto">
          <table className="w-full text-left" data-testid="admin-leads-table">
            <thead>
              <tr className="border-b border-epic-line font-mono text-[10px] uppercase tracking-[0.2em] text-epic-sub">
                <th className="px-4 md:px-5 py-4">#</th>
                <th className="px-4 md:px-5 py-4">Name</th>
                <th className="px-4 md:px-5 py-4">Email</th>
                <th className="px-4 md:px-5 py-4">Status</th>
                <th className="px-4 md:px-5 py-4">Note</th>
                <th className="px-4 md:px-5 py-4">Signed</th>
                <th className="px-4 md:px-5 py-4">Submitted</th>
              </tr>
            </thead>
            <tbody>
              {leads.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center font-mono text-sm text-epic-sub">
                    No leads yet.
                  </td>
                </tr>
              ) : (
                leads.map((l, i) => (
                  <tr key={l.id} className="border-b border-epic-line last:border-0 hover:bg-epic-bg/40 transition-colors align-top" data-testid={`lead-row-${i}`}>
                    <td className="px-4 md:px-5 py-4 font-mono text-xs text-epic-sub">{String(i + 1).padStart(2, "0")}</td>
                    <td className="px-4 md:px-5 py-4 font-mono text-sm text-epic-text whitespace-nowrap">{l.first_name} {l.last_name}</td>
                    <td className="px-4 md:px-5 py-4 font-mono text-sm text-epic-accent">{l.email}</td>
                    <td className="px-4 md:px-5 py-4">
                      <select
                        data-testid={`lead-status-${i}`}
                        value={l.status || "New"}
                        onChange={(e) => patchLead(l.id, { status: e.target.value })}
                        className={`bg-epic-bg border border-epic-line focus:border-epic-accent outline-none px-2 py-1.5 font-mono text-xs ${STATUS_COLORS[l.status] || "text-epic-text"}`}
                      >
                        {STATUSES.map((s) => (
                          <option key={s} value={s} className="bg-epic-bg text-epic-text">{s}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 md:px-5 py-4 min-w-[220px]">
                      <input
                        type="text"
                        data-testid={`lead-note-${i}`}
                        defaultValue={l.note || ""}
                        placeholder="Add a note…"
                        onBlur={(e) => { if (e.target.value !== (l.note || "")) patchLead(l.id, { note: e.target.value }); }}
                        className="w-full bg-epic-bg border border-epic-line focus:border-epic-accent outline-none px-3 py-1.5 font-mono text-xs text-epic-text placeholder:text-epic-sub/40"
                      />
                    </td>
                    <td className="px-4 md:px-5 py-4">
                      {l.signed ? (
                        <span className="inline-flex items-center gap-1.5 font-mono text-xs text-green-400" data-testid={`lead-signed-${i}`}>
                          <CheckCircle2 className="w-4 h-4" /> Signed
                        </span>
                      ) : (
                        <span className="font-mono text-xs text-epic-sub/40">—</span>
                      )}
                    </td>
                    <td className="px-4 md:px-5 py-4 font-mono text-xs text-epic-sub whitespace-nowrap">{fmt(l.timestamp)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
